import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeftIcon, BuildingIcon, TruckIcon, PackageIcon, ClipboardIcon, MapPinIcon, PhoneIcon, CalendarIcon, FileTextIcon, AlertOctagonIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import ShipmentStatusBadge from '../components/shipments/ShipmentStatusBadge';
import ShipmentHorizontalTimeline from '../components/shipments/ShipmentHorizontalTimeline';
import ShipmentMap from '../components/shipments/ShipmentMap';
import ShipmentCharges from '../components/shipments/ShipmentCharges';
import DocumentViewer from '../components/shipments/DocumentViewer';
const ShipmentDetail = () => {
  const {
    id
  } = useParams();
  const [loading, setLoading] = useState(true);
  const [shipment, setShipment] = useState<any>(null);
  const [showTracking, setShowTracking] = useState(false);
  const [showDocuments, setShowDocuments] = useState(false);
  useEffect(() => {
    // Simulate API call
    const timer = setTimeout(() => {
      setShipment({
        id: 'ORD123456',
        status: 'In Transit',
        createdAt: '2024-01-15T08:00:00Z',
        customer: 'Acme Corporation',
        equipmentType: 'Dry Van',
        weight: '42,000 lbs',
        commodity: 'General Merchandise',
        origin: {
          name: 'Acme Distribution Center',
          address: '123 Warehouse Ave',
          city: 'Chicago',
          state: 'IL',
          zip: '60601',
          contact: 'John Smith',
          phone: '(312) 555-0123',
          appointmentTime: '2024-01-15T10:00:00Z'
        },
        destination: {
          name: 'Regional Warehouse',
          address: '456 Industrial Pkwy',
          city: 'Dallas',
          state: 'TX',
          zip: '75201',
          contact: 'Jane Doe',
          phone: '(214) 555-0123',
          appointmentTime: '2024-01-17T14:00:00Z'
        },
        carrier: {
          name: 'Fast Freight Inc.',
          mcNumber: 'MC123456',
          driver: {
            name: 'Mike Johnson',
            phone: '(555) 123-4567'
          }
        },
        currentLocation: {
          lat: 39.8283,
          lng: -98.5795,
          status: 'In Transit',
          timestamp: '2024-01-16T12:30:00Z'
        },
        locations: {
          origin: {
            lat: 41.8781,
            lng: -87.6298,
            name: 'Chicago, IL',
            status: 'Picked Up'
          },
          destination: {
            lat: 32.7767,
            lng: -96.797,
            name: 'Dallas, TX',
            status: 'Awaiting Delivery'
          }
        },
        charges: [{
          id: '1',
          description: 'Line Haul',
          quantity: 1,
          rate: 2500.0,
          amount: 2500.0
        }, {
          id: '2',
          description: 'Fuel Surcharge',
          quantity: 1,
          rate: 375.0,
          amount: 375.0
        }, {
          id: '3',
          description: 'Stop Off',
          quantity: 2,
          rate: 75.0,
          amount: 150.0
        }]
      });
      setLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, [id]);
  if (loading) {
    return <div className="py-2" data-id="element-1315">
        <div className="animate-pulse" data-id="element-1316">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4" data-id="element-1317"></div>
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4" data-id="element-1318"></div>
          <div className="space-y-4" data-id="element-1319">
            {[1, 2, 3].map(i => <div key={i} className="bg-white rounded-lg p-4" data-id="element-1320">
                <div className="h-4 bg-gray-200 rounded w-1/4 mb-4" data-id="element-1321"></div>
                <div className="space-y-3" data-id="element-1322">
                  <div className="h-4 bg-gray-200 rounded w-full" data-id="element-1323"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6" data-id="element-1324"></div>
                </div>
              </div>)}
          </div>
        </div>
      </div>;
  }
  if (!shipment) {
    return <div className="py-2" data-id="element-1325">
        <div className="bg-red-50 border-l-4 border-red-500 p-4" data-id="element-1326">
          <p className="text-red-700" data-id="element-1327">Shipment not found</p>
        </div>
      </div>;
  }
  const timelinePoints = [{
    id: '1',
    title: 'Origin',
    description: 'Chicago, IL',
    timestamp: '2024-01-15 10:15 AM',
    status: 'completed' as const,
    type: 'pickup' as const
  }, {
    id: '2',
    title: 'In Transit',
    description: 'En route to Dallas',
    timestamp: 'Current',
    status: 'current' as const,
    type: 'in_transit' as const
  }, {
    id: '3',
    title: 'Destination',
    description: 'Dallas, TX',
    timestamp: '2024-01-17 2:00 PM',
    status: 'upcoming' as const,
    type: 'delivery' as const
  }];
  return <div className="py-2" data-id="element-1328">
      {/* Header */}
      <div className="mb-2" data-id="element-1329">
        <Link to="/shipments" className="flex items-center text-gray-600 hover:text-gray-900 text-sm" data-id="element-1330">
          <ArrowLeftIcon size={14} className="mr-1" data-id="element-1331" /> Back to Shipments
        </Link>
        <div className="mt-1 flex flex-col sm:flex-row sm:items-center sm:justify-between" data-id="element-1332">
          <div className="flex items-center gap-2" data-id="element-1333">
            <h1 className="text-xl font-semibold text-gray-800" data-id="element-1334">
              Shipment {shipment?.id}
            </h1>
            <ShipmentStatusBadge status={shipment?.status} data-id="element-1335" />
          </div>
          <p className="text-sm text-gray-600" data-id="element-1336">
            Created on {new Date(shipment?.createdAt).toLocaleDateString()}
          </p>
        </div>
      </div>
      {/* Actions Bar */}
      <div className="bg-white rounded-lg shadow-sm mb-3 p-2" data-id="element-1337">
        <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-1338">
          <div className="flex flex-wrap gap-2" data-id="element-1339">
            <button onClick={() => setShowDocuments(true)} className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-50 rounded-md hover:bg-gray-100 flex items-center" data-id="element-1340">
              <FileTextIcon size={16} className="mr-1.5" data-id="element-1341" />
              View Documents
            </button>
            <button onClick={() => setShowTracking(!showTracking)} className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-50 rounded-md hover:bg-gray-100 flex items-center" data-id="element-1342">
              <TruckIcon size={16} className="mr-1.5" data-id="element-1343" />
              Track Shipment
            </button>
          </div>
          <button className="px-3 py-1.5 text-sm font-medium text-red-700 bg-red-50 rounded-md hover:bg-red-100 flex items-center" data-id="element-1344">
            <AlertOctagonIcon size={16} className="mr-1.5" data-id="element-1345" />
            Report Issue
          </button>
        </div>
      </div>
      {/* Document Viewer Modal */}
      <DocumentViewer isOpen={showDocuments} onClose={() => setShowDocuments(false)} data-id="element-1346" />
      {/* Progress Timeline */}
      <div className="bg-white rounded-lg shadow-sm mb-3" data-id="element-1347">
        <div className="p-2" data-id="element-1348">
          <ShipmentHorizontalTimeline points={timelinePoints} data-id="element-1349" />
        </div>
      </div>
      {/* Tracking Map - Collapsible */}
      <div className="bg-white rounded-lg shadow-sm mb-3" data-id="element-1350">
        <button onClick={() => setShowTracking(!showTracking)} className="w-full p-3 flex items-center justify-between border-b border-gray-200" data-id="element-1351">
          <h2 className="text-lg font-medium text-gray-900 flex items-center" data-id="element-1352">
            <MapPinIcon size={20} className="mr-2 text-primary-700" data-id="element-1353" />
            Tracking Map
          </h2>
          {showTracking ? <ChevronUpIcon size={20} className="text-gray-500" data-id="element-1354" /> : <ChevronDownIcon size={20} className="text-gray-500" data-id="element-1355" />}
        </button>
        {showTracking && <div className="p-3" data-id="element-1356">
            {shipment?.locations && <ShipmentMap origin={shipment.locations.origin} destination={shipment.locations.destination} currentLocation={shipment.currentLocation} data-id="element-1357" />}
          </div>}
      </div>
      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-id="element-1358">
        {/* Shipment Overview & Charges */}
        <div className="space-y-3" data-id="element-1359">
          <div className="bg-white rounded-lg shadow-sm p-3" data-id="element-1360">
            <h2 className="text-lg font-medium text-gray-900 flex items-center mb-3" data-id="element-1361">
              <ClipboardIcon size={20} className="mr-2 text-primary-700" data-id="element-1362" />
              Shipment Overview
            </h2>
            <div className="grid grid-cols-2 gap-4" data-id="element-1363">
              <div data-id="element-1364">
                <p className="text-sm font-medium text-gray-500" data-id="element-1365">Customer</p>
                <p className="text-sm text-gray-900" data-id="element-1366">{shipment.customer}</p>
              </div>
              <div data-id="element-1367">
                <p className="text-sm font-medium text-gray-500" data-id="element-1368">Equipment</p>
                <p className="text-sm text-gray-900" data-id="element-1369">
                  {shipment.equipmentType}
                </p>
              </div>
              <div data-id="element-1370">
                <p className="text-sm font-medium text-gray-500" data-id="element-1371">Weight</p>
                <p className="text-sm text-gray-900" data-id="element-1372">{shipment.weight}</p>
              </div>
              <div data-id="element-1373">
                <p className="text-sm font-medium text-gray-500" data-id="element-1374">Commodity</p>
                <p className="text-sm text-gray-900" data-id="element-1375">{shipment.commodity}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-3" data-id="element-1376">
            <h2 className="text-lg font-medium text-gray-900 flex items-center mb-3" data-id="element-1377">
              <FileTextIcon size={20} className="mr-2 text-primary-700" data-id="element-1378" />
              Charges
            </h2>
            <ShipmentCharges charges={shipment?.charges} data-id="element-1379" />
          </div>
        </div>
        {/* Locations & Carrier Info */}
        <div className="space-y-3" data-id="element-1380">
          <div className="bg-white rounded-lg shadow-sm p-3" data-id="element-1381">
            <h2 className="text-lg font-medium text-gray-900 flex items-center mb-3" data-id="element-1382">
              <MapPinIcon size={20} className="mr-2 text-primary-700" data-id="element-1383" />
              Locations
            </h2>
            <div className="grid grid-cols-2 gap-4" data-id="element-1384">
              {/* Origin */}
              <div data-id="element-1385">
                <h3 className="text-sm font-medium text-gray-900 mb-2" data-id="element-1386">
                  Origin
                </h3>
                <div className="space-y-2" data-id="element-1387">
                  <p className="text-sm font-medium text-gray-900" data-id="element-1388">
                    {shipment.origin.name}
                  </p>
                  <p className="text-sm text-gray-500" data-id="element-1389">
                    {shipment.origin.address}
                  </p>
                  <p className="text-sm text-gray-500" data-id="element-1390">
                    {shipment.origin.city}, {shipment.origin.state}{' '}
                    {shipment.origin.zip}
                  </p>
                  <p className="text-sm text-gray-500 flex items-center" data-id="element-1391">
                    <PhoneIcon size={14} className="mr-1" data-id="element-1392" />
                    {shipment.origin.phone}
                  </p>
                  <p className="text-sm text-gray-500 flex items-center" data-id="element-1393">
                    <CalendarIcon size={14} className="mr-1" data-id="element-1394" />
                    {new Date(shipment.origin.appointmentTime).toLocaleString()}
                  </p>
                </div>
              </div>
              {/* Destination */}
              <div data-id="element-1395">
                <h3 className="text-sm font-medium text-gray-900 mb-2" data-id="element-1396">
                  Destination
                </h3>
                <div className="space-y-2" data-id="element-1397">
                  <p className="text-sm font-medium text-gray-900" data-id="element-1398">
                    {shipment.destination.name}
                  </p>
                  <p className="text-sm text-gray-500" data-id="element-1399">
                    {shipment.destination.address}
                  </p>
                  <p className="text-sm text-gray-500" data-id="element-1400">
                    {shipment.destination.city}, {shipment.destination.state}{' '}
                    {shipment.destination.zip}
                  </p>
                  <p className="text-sm text-gray-500 flex items-center" data-id="element-1401">
                    <PhoneIcon size={14} className="mr-1" data-id="element-1402" />
                    {shipment.destination.phone}
                  </p>
                  <p className="text-sm text-gray-500 flex items-center" data-id="element-1403">
                    <CalendarIcon size={14} className="mr-1" data-id="element-1404" />
                    {new Date(shipment.destination.appointmentTime).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-3" data-id="element-1405">
            <h2 className="text-lg font-medium text-gray-900 flex items-center mb-3" data-id="element-1406">
              <TruckIcon size={20} className="mr-2 text-primary-700" data-id="element-1407" />
              Carrier Information
            </h2>
            <div className="space-y-3" data-id="element-1408">
              <div data-id="element-1409">
                <p className="text-sm font-medium text-gray-900" data-id="element-1410">
                  {shipment.carrier.name}
                </p>
                <p className="text-sm text-gray-500" data-id="element-1411">
                  MC# {shipment.carrier.mcNumber}
                </p>
              </div>
              {shipment.carrier.driver && <div data-id="element-1412">
                  <p className="text-sm font-medium text-gray-900" data-id="element-1413">
                    Driver Information
                  </p>
                  <p className="text-sm text-gray-500" data-id="element-1414">
                    {shipment.carrier.driver.name}
                  </p>
                  <p className="text-sm text-gray-500 flex items-center" data-id="element-1415">
                    <PhoneIcon size={14} className="mr-1" data-id="element-1416" />
                    {shipment.carrier.driver.phone}
                  </p>
                </div>}
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default ShipmentDetail;